import { ChevronDown, ChevronUp, Minus, Plus, X } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";

import { fetchWrapper, InsiderTradingData } from "src/helpers";
import { getDisplayLabel } from "src/helpers/api/sharedFunction";
import { getFormattedDate } from "src/helpers/dateFormatter";
import { formatWithLocalString } from "src/helpers/numberFormatter";
import InsiderTradingSkeleton from "src/components/skeleton/InsiderTradingSkeleton";

interface InsiderTradingProps {
  toggleInsiderTradingModal: (symbol: string) => void;
  symbol: string;
  token: string;
}
interface Filterprops {
  holderCategory: string[];
  acquisitionType: string[];
}

const InsiderTradingModal = (props: InsiderTradingProps) => {
  const { toggleInsiderTradingModal, symbol, token } = props;
  const defaultFilters: Filterprops = {
    holderCategory: [],
    acquisitionType: [],
  };
  const [selectedFilters, setSelectedFilters] =
    useState<Filterprops>(defaultFilters);
  const [loading, setLoading] = useState(false);

  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterOptions, setFilterOptions] = useState({
    holderCategory: [] as string[],
    acquisitionType: [] as string[],
  });
  const _filterTypes = ["holderCategory", "acquisitionType"] as const;
  type FilterType = (typeof _filterTypes)[number];

  const [records, setRecords] = useState<InsiderTradingData[]>([]);
  const [toggleSymbolExpansion, setToggleSymbolExpansion] = useState<
    Record<string, boolean>
  >({});
  const dropdownRef = useRef<HTMLDivElement>(null);

  const getData = async () => {
    setLoading(true);
    const activeFilters = Object.entries(selectedFilters)
      .filter(([_, value]) => value !== "")
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join("&");

    const queryString = activeFilters ? `&${activeFilters}` : "";
    const response = await fetchWrapper.get(
      `/api/dashboard/insider_tradings?symbol=${encodeURIComponent(symbol)}${queryString}`,
      undefined,
      {
        Authorization: `Bearer ${token}`,
      },
    );

    setRecords(response.data);
    if (response.acquisition_modes || response.person_categories) {
      setFilterOptions({
        holderCategory:
          response.person_categories?.map(
            (p: { person_category: string }) => p.person_category,
          ) || [],
        acquisitionType:
          response.acquisition_modes?.map(
            (a: { acquisition_mode: string }) => a.acquisition_mode,
          ) || [],
      });
    }
    setLoading(false);
  };

  const toggleRow = (symbol: string) => {
    setToggleSymbolExpansion((prevState) => {
      if (prevState[symbol]) {
        return {};
      }
      return { [symbol]: true };
    });
  };

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, selectedFilters]);

  // Toggle dropdown
  const toggleDropdown = (dropdownName: string | null) => {
    setOpenDropdown(openDropdown === dropdownName ? null : dropdownName);
    setSearchTerm("");
  };
  // Get display text for dropdown
  const getDisplayText = (filterType: FilterType) => {
    const selected = selectedFilters[filterType] || [];
    const options = filterOptions[filterType] || [];
    if (selected.length === 0)
      return filterType === "acquisitionType"
        ? `Select Acquisition Mode`
        : `Select Category`;
    if (selected.length === options.length) return `All ${filterType}s`;
    if (selected.length > 2) return `${selected.length} selected`;
    return selected.map(getDisplayLabel).join(", ");
  };

  // Toggle filter selection
  const toggleFilterSelection = (filterType: FilterType, item: string) => {
    setSelectedFilters((prev) => {
      const currentSelection = Array.isArray(prev[filterType])
        ? prev[filterType]
        : [];
      const newSelection = currentSelection.includes(item)
        ? currentSelection.filter((i) => i !== item)
        : [...currentSelection, item];

      return {
        ...prev,
        [filterType]: newSelection,
      };
    });
  };

  // Toggle select all for a filter type
  const toggleSelectAll = (filterType: FilterType) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [filterType]:
        prev[filterType]?.length === filterOptions[filterType]?.length
          ? []
          : [...filterOptions[filterType]],
    }));
  };

  // Filter items based on search term
  const filterItems = (items: string[], searchTerm: string) => {
    if (!searchTerm) return items;
    return items.filter((item) =>
      item.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 backdrop-blur-sm"
      onClick={() => toggleInsiderTradingModal("")}
    >
      <div
        className="bg-white dark:bg-[#1E1E2D] rounded-lg shadow-lg w-full max-w-7xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Insider Trading
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              (Past 30 Days)
            </p>
          </div>
          <button
            onClick={() => toggleInsiderTradingModal("")}
            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 cursor-pointer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Insider Trading Filter Section */}
        {loading ? (
          <InsiderTradingSkeleton />
        ) : (
          <div className="">
            {/* <h5 className='flex justify-between items-center w-full p-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-[#f4f5f8] dark:bg-[#30324e] tracking-wider'>Insider Trading Filter</h5> */}
            <div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4 p-4"
              ref={dropdownRef}
            >
              {/* Holder Category Dropdown */}
              <div className="w-full">
                <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                  Category
                </label>
                <div className="relative cursor">
                  <button
                    type="button"
                    onClick={() => toggleDropdown("holderCategory")}
                    className="w-full bg-white dark:bg-[#1E1E2D] pl-3 pr-2 py-2 border border-[#a0a5bb] rounded-md text-left flex items-center justify-between text-sm font-figtree text-gray-700 dark:text-white cursor-pointer"
                  >
                    <span className="truncate">
                      {getDisplayText("holderCategory")}
                    </span>
                    {openDropdown === "holderCategory" ? (
                      <ChevronUp className="h-4 w-4 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    )}
                  </button>
                  {openDropdown === "holderCategory" && (
                    <div className="absolute z-10 mt-1 w-full bg-white dark:bg-[#1E1E2D] border border-gray-200 dark:border-gray-600 rounded-md shadow-lg">
                      <div className="p-2">
                        <div className="relative mb-2">
                          <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search categories..."
                            className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          <label className="flex items-center px-2 py-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded cursor-pointer">
                            <input
                              type="checkbox"
                              checked={
                                selectedFilters.holderCategory?.length ===
                                filterOptions.holderCategory.length
                              }
                              onChange={() => toggleSelectAll("holderCategory")}
                              className="form-checkbox h-4 w-4 text-blue-500 cursor-pointer"
                            />
                            <span className="ml-2 text-sm font-medium">
                              Select All
                            </span>
                          </label>
                          <div className="border-t border-gray-200 dark:border-gray-600 my-1"></div>
                          {filterItems(
                            filterOptions.holderCategory,
                            searchTerm,
                          ).map((item: string) => (
                            <label
                              key={item}
                              className="flex items-center px-2 py-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={
                                  selectedFilters.holderCategory?.includes(
                                    item,
                                  ) || false
                                }
                                onChange={() =>
                                  toggleFilterSelection("holderCategory", item)
                                }
                                className="form-checkbox h-4 w-4 text-blue-500 cursor-pointer"
                              />
                              <span className="ml-2 text-sm">
                                {getDisplayLabel(item)}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Acquisition Type Dropdown */}
              <div className="w-full">
                <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                  Acquisition Mode
                </label>
                <div className="relative cursor">
                  <button
                    type="button"
                    onClick={() => toggleDropdown("acquisitionType")}
                    className="w-full bg-white dark:bg-[#1E1E2D] pl-3 pr-2 py-2 border border-[#a0a5bb] rounded-md text-left flex items-center justify-between text-sm font-figtree text-gray-700 dark:text-white cursor-pointer"
                  >
                    <span className="truncate">
                      {getDisplayText("acquisitionType")}
                    </span>
                    {openDropdown === "acquisitionType" ? (
                      <ChevronUp className="h-4 w-4 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    )}
                  </button>
                  {openDropdown === "acquisitionType" && (
                    <div className="absolute z-10 mt-1 w-full bg-white dark:bg-[#1E1E2D] border border-gray-200 dark:border-gray-600 rounded-md shadow-lg">
                      <div className="p-2">
                        <div className="relative mb-2">
                          <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search types..."
                            className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          <label className="flex items-center px-2 py-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded cursor-pointer">
                            <input
                              type="checkbox"
                              checked={
                                selectedFilters.acquisitionType?.length ===
                                filterOptions.acquisitionType.length
                              }
                              onChange={() =>
                                toggleSelectAll("acquisitionType")
                              }
                              className="form-checkbox h-4 w-4 text-blue-500 cursor-pointer"
                            />
                            <span className="ml-2 text-sm font-medium">
                              Select All
                            </span>
                          </label>
                          <div className="border-t border-gray-200 dark:border-gray-600 my-1"></div>
                          {filterItems(
                            filterOptions.acquisitionType,
                            searchTerm,
                          ).map((item: string) => (
                            <label
                              key={item}
                              className="flex items-center px-2 py-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={
                                  selectedFilters.acquisitionType?.includes(
                                    item,
                                  ) || false
                                }
                                onChange={() =>
                                  toggleFilterSelection("acquisitionType", item)
                                }
                                className="form-checkbox h-4 w-4 text-blue-500 cursor-pointer"
                              />
                              <span className="ml-2 text-sm">
                                {getDisplayLabel(item)}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="p-4">
              <div className="rounded-md border border-[#d0d4e4] dark:border-[#394564]">
                <div className="overflow-x-auto bg-white dark:bg-[#121419] h-[300px]">
                  <table className="w-full min-w-full min-w-max">
                    <thead className="sticky top-0">
                      <tr className="bg-[#f4f5f8] dark:bg-[#30324e] border-b border-[#d0d4e4] dark:border-[#394564]">
                        <th className="text-left py-3 px-6 text-sm font-semibold normal-case font-medium text-black dark:text-[#bcc2d0] uppercase tracking-wider font-figtree">
                          Symbol
                        </th>
                        <th className="text-left py-3 px-6 text-sm font-semibold normal-case font-medium text-black dark:text-[#bcc2d0] uppercase tracking-wider font-figtree">
                          Type
                        </th>
                        <th className="text-left py-3 px-6 text-sm font-semibold normal-case font-medium text-black dark:text-[#bcc2d0] uppercase tracking-wider font-figtree">
                          Qty.
                        </th>
                        <th className="text-left py-3 px-6 text-sm font-semibold normal-case font-medium text-black dark:text-[#bcc2d0] uppercase tracking-wider font-figtree">
                          Value
                        </th>
                        <th className="text-left py-3 px-6 text-sm font-semibold normal-case font-medium text-black dark:text-[#bcc2d0] uppercase tracking-wider font-figtree">
                          Prior %
                        </th>
                        <th className="text-left py-3 px-6 text-sm font-semibold normal-case font-medium text-black dark:text-[#bcc2d0] uppercase tracking-wider font-figtree">
                          Post %
                        </th>
                        <th className="text-left py-3 px-6 text-sm font-semibold normal-case font-medium text-black dark:text-[#bcc2d0] uppercase tracking-wider font-figtree">
                          Var %
                        </th>
                        <th className="text-left py-3 px-6 text-sm font-semibold normal-case font-medium text-black dark:text-[#bcc2d0] uppercase tracking-wider font-figtree">
                          Acquisition Mode
                        </th>
                        <th className="text-left py-3 px-6 text-sm font-semibold normal-case font-medium text-black dark:text-[#bcc2d0] uppercase tracking-wider font-figtree">
                          Person Category
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {records.length > 0 &&
                        records.map((record, index) => (
                          <React.Fragment key={"insider-trading-" + index}>
                            <tr
                              className={`border-b border-[#d0d4e4] dark:border-[#394564] ${toggleSymbolExpansion ? "cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700" : ""}`}
                              onClick={() => toggleRow(record.transaction_type)}
                            >
                              <td className="py-3 px-6 transition-colors duration-200 font-figtree text-sm uppercase">
                                <div className="flex items-center">
                                  <span className="mr-2">
                                    {toggleSymbolExpansion[
                                      record.transaction_type
                                    ] ? (
                                      <Minus size={14} />
                                    ) : (
                                      <Plus size={14} />
                                    )}
                                  </span>
                                  {record.stock_symbol}
                                </div>
                              </td>
                              <td className="py-3 px-6 transition-colors duration-200 font-figtree text-sm">
                                {record.transaction_type}
                              </td>
                              <td className="py-3 px-6 transition-colors duration-200 font-figtree text-sm">
                                {formatWithLocalString(
                                  record.acquired_disclosed_security_count,
                                )}
                              </td>
                              <td className="py-3 px-6 transition-colors duration-200 font-figtree text-sm">
                                ₹
                                {formatWithLocalString(
                                  record.acquired_disclosed_security_value,
                                )}
                              </td>
                              <td className="py-3 px-6 transition-colors duration-200 font-figtree text-sm">
                                {record.prior_shareholding_percent}
                              </td>
                              <td className="py-3 px-6 transition-colors duration-200 font-figtree text-sm">
                                {record.post_shareholding_percent}
                              </td>
                              <td
                                className={`py-3 px-6 transition-colors duration-200 font-figtree text-sm ${
                                  parseFloat(record.percentage_point_change) >=
                                  0
                                    ? "text-green-500"
                                    : "text-red-500"
                                }`}
                              >
                                {record.percentage_point_change}
                              </td>
                              <td className="py-3 px-6 transition-colors duration-200 font-figtree text-sm">
                                {getDisplayLabel(record.acquisition_mode)}
                              </td>
                              <td className="py-3 px-6 transition-colors duration-200 font-figtree text-sm font-medium">
                                {getDisplayLabel(record.person_category)}
                              </td>
                            </tr>
                            {toggleSymbolExpansion[record.transaction_type] &&
                              record.children.map((record, index) => (
                                <tr
                                  key={"trading-" + index}
                                  className="border-b border-[#d0d4e4] dark:border-[#394564]"
                                >
                                  <td className="text-end py-2 px-6 transition-colors duration-200 font-figtree text-sm text-gray-600 dark:text-gray-300">
                                    <div className="flex flex-col gap-1">
                                      <span>
                                        <strong>From:</strong>{" "}
                                        {getFormattedDate(
                                          record.acquisition_from_date.toString(),
                                        )}
                                      </span>
                                      <span>
                                        <strong>To:</strong>{" "}
                                        {getFormattedDate(
                                          record.acquisition_to_date.toString(),
                                        )}
                                      </span>
                                    </div>
                                  </td>

                                  <td className="py-3 px-6 dark:group-hover:bg-[#394564]/50 transition-colors duration-200 font-figtree text-sm">
                                    {record.transaction_type}
                                  </td>
                                  <td className="py-3 px-6 dark:group-hover:bg-[#394564]/50 transition-colors duration-200 font-figtree text-sm">
                                    {formatWithLocalString(
                                      record.acquired_disclosed_security_count,
                                    )}
                                  </td>
                                  <td className="py-3 px-6 dark:group-hover:bg-[#394564]/50 transition-colors duration-200 font-figtree text-sm">
                                    ₹
                                    {formatWithLocalString(
                                      record.acquired_disclosed_security_value,
                                    )}
                                  </td>
                                  <td className="py-3 px-6 dark:group-hover:bg-[#394564]/50 transition-colors duration-200 font-figtree text-sm">
                                    {record.prior_shareholding_percent}
                                  </td>
                                  <td className="py-3 px-6 dark:group-hover:bg-[#394564]/50 transition-colors duration-200 font-figtree text-sm">
                                    {record.post_shareholding_percent}
                                  </td>
                                  <td
                                    className={`py-3 px-6 transition-colors duration-200 font-figtree text-sm ${
                                      parseFloat(
                                        record.percentage_point_change,
                                      ) >= 0
                                        ? "text-green-500"
                                        : "text-red-500"
                                    }`}
                                  >
                                    {record.percentage_point_change}
                                  </td>
                                  <td className="py-3 px-6 dark:group-hover:bg-[#394564]/50 transition-colors duration-200 font-figtree text-sm">
                                    {getDisplayLabel(record.acquisition_mode)}
                                  </td>
                                  <td className="py-3 px-6 dark:group-hover:bg-[#394564]/50 transition-colors duration-200 font-figtree text-sm font-medium">
                                    {getDisplayLabel(record.person_category)}
                                  </td>
                                </tr>
                              ))}
                          </React.Fragment>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
export default React.memo(InsiderTradingModal);
